import { ExportDrop } from '../../../../shared/components/cards/export-drop/ExportDrop'
import React, { useEffect, useState } from 'react'
import { Switch } from 'antd'
import { useAppDispatch, useAppSelector } from '../../../../app/store/hooks/Redux'
import { IPpcComplianceFilter } from '../../model/types/PpcComplianceFilter.interface'
import {
  selectPpcComplianceFilter,
  selectProject,
  setPpcComplianceFilter,
} from '../../model/ppcComplianceSlice'
import { BpTooltip } from '../../../../shared/components/cards/tooltip/BpTooltip'
import {
  ExportNonCompliantAffiliatesArg,
  useExportNonCompliantAffiliates,
} from '../../../../app/api'
import { useTranslation } from 'react-i18next'
import { AiButton } from '../../../../shared/components/ai/ai-button/AiButton'
import { useAiChat } from '../../../../shared/components/ai/ai-provider/AiProvider'
import clsx from 'clsx'
import { AlertingChip } from '../../../../lib/widgets/alerting-chip/AlertingChip'
import styles from './PpcCompliancePanel.module.scss'

export const PpcCompliancePanel = () => {
  const { t } = useTranslation()
  const { handleSendMessage } = useAiChat()
  const currentProject = useAppSelector(selectProject)
  const dispatch = useAppDispatch()
  const filters = useAppSelector<IPpcComplianceFilter>(selectPpcComplianceFilter)
  const {
    project,
    country,
    affiliate,
    network,
    dateRange,
    site,
    engine,
    browser,
    city,
    hidden,
    adHijackers,
    shoppingAds,
    status,
    affiliateRule,
    keyword,
    advertiserName,
    reversed_filters,
  } = filters

  const [queryParams, setQueryParams] = useState<Partial<ExportNonCompliantAffiliatesArg>>()

  const [exportNonCompliantAffiliates, { isFetching: exportLoading }] =
    useExportNonCompliantAffiliates()

  useEffect(() => {
    setQueryParams({
      ...queryParams,
      projectId: project ? Number(project) : undefined,
      countryId: country || undefined,
      affiliateId: affiliate || undefined,
      network: network,
      date: dateRange,
      site: site,
      engineId: engine || undefined,
      browserId: browser || undefined,
      cityId: city || undefined,
      isHidden: hidden,
      isHijack: adHijackers,
      showShoppingAds: shoppingAds,
      statusId: status ? Number(status) : undefined,
      affiliateRuleId: affiliateRule ? Number(affiliateRule) : undefined,
      keyword,
      advertiserId: advertiserName || undefined,
      reversedFilters: reversed_filters || undefined,
    })
  }, [filters])

  useEffect(() => {
    if (!currentProject.has_shopping_ads) {
      dispatch(setPpcComplianceFilter({ ...filters, shoppingAds: false }))
    }
  }, [currentProject])

  return (
    <div className={clsx(styles.root)}>
      {
        {
          bluepear: (
            <AiButton
              unstyled
              onClick={() =>
                handleSendMessage({
                  message:
                    'Please provide a clear guide on how to use the PPC Compliance Report and explain its value.',
                })
              }
            >
              <span className={clsx(styles.aiButtonText)}>How the report works</span>
            </AiButton>
          ),
          robuscan: null,
          default: null,
        }[process.env.REACT_APP_PROJECT ?? 'default']
      }
      {currentProject.has_shopping_ads && (
        <span className={clsx(styles.showPlaRow)}>
          <Switch
            checked={shoppingAds}
            defaultChecked={shoppingAds}
            onClick={(e) => {
              dispatch(setPpcComplianceFilter({ ...filters, shoppingAds: e }))
            }}
          ></Switch>
          {t('buttons.showPLAs')}
          <BpTooltip title={t('buttons.other.showPLAsTooltip')} />
        </span>
      )}
      <AlertingChip projectId={Number(project) || undefined} type={'affiliates'} />
      <ExportDrop
        columns={[
          {
            label: t('table.columns.affiliates'),
            bold: true,
            child: [
              { keys: ['affiliate'], label: t('table.columns.affiliateId') },
              { keys: ['ads_count'], label: t('table.columns.adsAnalyzed') },
              {
                keys: ['network_name'],
                label: t('table.columns.network'),
                tooltip: t('pages.PPCCompliance.networkTooltip'),
              },
              { keys: ['advertisers'], label: t('table.columns.advertiser') },
              { keys: ['status_id'], label: t('table.columns.status') },
              { keys: ['first_found'], label: t('table.columns.firstFound') },
              { keys: ['last_found'], label: t('table.columns.lastFound') },
            ],
          },
          {
            keys: ['latest_examples'],
            label: t('table.columns.latestExamples'),
            tooltip: t('pages.PPCCompliance.latestExamplesTooltip'),
            bold: true,
          },
        ]}
        loading={exportLoading}
        onExport={(exportParams) =>
          exportNonCompliantAffiliates({ ...queryParams, ...exportParams })
        }
      />
    </div>
  )
}
